import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdName } from '@shared/models/id-name';
import { MaterialShadeSystem } from '@shared/models/material-shade-system';
import { ShadeSystem } from '@shared/models/shade-system';
import { MATERIAL_SHADE_SYSTEM_RULES } from '@shared/rules/rules-tokens';
import { ShellQuery } from '@shared/store/shell/shell-query';

/**
 * Material / shade system rules derived from {@link MATERIAL_SHADE_SYSTEM_RULES}.
 * Extracted from {@link RxRulesService} for reuse (e.g. intuitive-rx and v1 tooth editor) without depending on the deprecated service.
 */
@Injectable({ providedIn: 'root' })
export class RxShadeSystemRulesService {
	private materialShadeSystemRulesTranslated: MaterialShadeSystem[] = this.materialShadeSystemRules;
	private readonly defaultMaterialId = -1;

	constructor(
		@Inject(MATERIAL_SHADE_SYSTEM_RULES) private readonly materialShadeSystemRules: MaterialShadeSystem[],
		private readonly translateService: TranslateService,
		private readonly shellQuery: ShellQuery
	) {}

	/** Refreshes translated shade system labels (call when locale changes; invoked from {@link RxRulesService#translateSelectOptions}). */
	translateShadeSystemOptions(): void {
		this.materialShadeSystemRulesTranslated = this.materialShadeSystemRules.map(materialShadeSystem => {
			const shadeSystems = materialShadeSystem.ShadeSystems.map(shadeSystem =>
				this.getTranslatedObject<ShadeSystem>(shadeSystem, 'ShadeSystem')
			);

			return { ...materialShadeSystem, ShadeSystems: shadeSystems };
		});
	}

	getMaterialShadeSystemRuleByMaterialId({ materialId }: { materialId: number }): MaterialShadeSystem | undefined {
		return this.materialShadeSystemRulesTranslated.find(rule => rule.Id === materialId);
	}

	getShadeSystemOptionsByMaterialId({ materialId }: { materialId: number }): ShadeSystem[] | undefined {
		return this.getMaterialShadeSystemRuleByMaterialId({ materialId })?.ShadeSystems;
	}

	getDefaultShadeSystemSelectionId({ materialId }: { materialId: number }): number | undefined {
		return this.getMaterialShadeSystemRuleByMaterialId({ materialId })?.DefaultShadeSystem;
	}

	getDefaultShadeSystemOptions(): ShadeSystem[] {
		return this.getMaterialShadeSystemRuleByMaterialId({ materialId: this.defaultMaterialId })?.ShadeSystems ?? [];
	}

	getShadeSystemSelection(shadeSystemIdFromTooth?: number): ShadeSystem {
		const defaultShadeSystemOptions = this.getDefaultShadeSystemOptions();

		const shadeSystemFromUserSetting = this.shellQuery.userSettings?.ShadeSystemId;
		const userSettingsShadeSystemOption = defaultShadeSystemOptions?.find(option => option.Id === shadeSystemFromUserSetting);

		const shadeSystemSelection = shadeSystemIdFromTooth
			? defaultShadeSystemOptions.find(option => option.Id === shadeSystemIdFromTooth)
			: undefined;

		return shadeSystemSelection || userSettingsShadeSystemOption || defaultShadeSystemOptions[0];
	}

	getShadeSystemOptionsAndSelection(shadeSystemIdFromTooth?: number): { shadeSystem: ShadeSystem; options: ShadeSystem[] } {
		return {
			shadeSystem: this.getShadeSystemSelection(shadeSystemIdFromTooth),
			options: this.getDefaultShadeSystemOptions()
		};
	}

	getIncisalBodyGingivalOptions({ materialId, shadeSystemId }: { materialId: number; shadeSystemId: number }): IdName[] | undefined {
		let shadeSystemOptions = this.getShadeSystemOptionsByMaterialId({ materialId });

		if (!shadeSystemOptions || shadeSystemOptions.length === 0) {
			shadeSystemOptions = this.getDefaultShadeSystemOptions();
		}

		const shadeSystem: ShadeSystem | undefined = shadeSystemOptions?.find(shadeOption => shadeOption.Id === shadeSystemId);

		return shadeSystem?.Options?.map((shadeSystemOption: string) => ({
			Id: shadeSystemOption,
			Name: shadeSystemOption
		}));
	}

	getStumpShadeOptions(): IdName[] | undefined {
		const stumpShades: string[] | undefined = this.getMaterialShadeSystemRuleByMaterialId({
			materialId: this.defaultMaterialId
		})?.StumpShade;

		return stumpShades?.map((stumpShade: string) => ({
			Id: stumpShade,
			Name: stumpShade
		}));
	}

	private getTranslatedObject<T extends { Name: string }>(object: T, sectionName: string): T {
		return {
			...object,
			Name: this.translateService.instant(`${sectionName}.${object.Name}`)
		};
	}
}
